import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HttpResponse } from '@angular/common/http';
import { RepackService } from './repack.service';
import { BulkChildMappingComponent } from './components/bulk-child-mapping/bulk-child-mapping.component';
import { CanComponentDeactivate } from '../../../Guard/pending-changes.guard';

@Component({
  selector: 'app-repack',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, BulkChildMappingComponent],
  templateUrl: './repack.component.html',
  styleUrl: './repack.component.scss',
})
export class RepackComponent implements OnInit, CanComponentDeactivate {
  private repackService = inject(RepackService);

  // Only present while the mapping tab is rendered; the list tab has nothing
  // to lose, so an absent child means "safe to leave".
  @ViewChild(BulkChildMappingComponent)
  private mapping?: BulkChildMappingComponent;

  activeTab: 'bulk-breakup' | 'mapping' = 'bulk-breakup';
  currentPage = 1;
  pageSize = 10;
  repackList: any[] = [];
  repacksLoading = false;
  pageCount = 1;
  searchTerm = '';

  ngOnInit(): void {
    this.loadRepacks();
  }

  /**
   * PendingChangesGuard hook for the /repack route. The unsaved work lives in
   * the mapping tab's child component, so the prompt is delegated to it.
   */
  async canDeactivate(): Promise<boolean> {
    return this.mapping ? this.mapping.canDeactivate() : true;
  }

  /**
   * Switching tabs destroys the mapping component via *ngIf, which loses the
   * same edits a navigation would — so it asks with the same prompt.
   */
  async switchTab(tab: 'bulk-breakup' | 'mapping'): Promise<void> {
    if (tab === this.activeTab) return;
    if (!(await this.canDeactivate())) return;
    this.activeTab = tab;
  }

  loadRepacks() {
    this.repacksLoading = true;
    this.repackService.getRepacks().subscribe((res: any) => {
      this.repackList = res.data;
      this.pageCount = res.pageCount;
      this.repacksLoading = false;
    });
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const start = Math.max(2, this.currentPage - 2);
    const end = Math.min(this.pageCount - 1, this.currentPage + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  goToPage(page: number) {
    if (page < 1 || page > this.pageCount || page === this.currentPage) return;
    this.currentPage = page;
    this.loadRepacks();
  }

  pageSizeChange() {
    this.currentPage = 1;
    this.loadRepacks();
  }

  onSearch() {
    this.currentPage = 1;
    this.loadRepacks();
  }

  //   downloadRepack(id: string) {
  //     this.repackService.downloadRepack(id)
  //       .subscribe((response: HttpResponse<Blob>) => {
  //         const blob = response.body as Blob;
  //         const filename = this.extractFilename(response, id);

  //         const url = window.URL.createObjectURL(blob);
  //         const a = document.createElement('a');
  //         a.href = url;
  //         a.download = filename;
  //         a.click();
  //         window.URL.revokeObjectURL(url);
  //       });
  //   }

  private extractFilename(
    response: HttpResponse<Blob>,
    repackId: string
  ): string {
    const disposition = response.headers.get('Content-Disposition') || '';
    let match = disposition.match(/filename\*=([^;]+)/i);
    if (match && match[1]) {
      return decodeURIComponent(match[1].split("''").pop() || '');
    }
    match = disposition.match(/filename="?([^";]+)"?/i);
    if (match && match[1]) {
      return match[1];
    }
    return `repack-${repackId}.pdf`;
  }
}
